<template>
  <div
    class="grey-background content-container category-grid surprise-section"
    :class="searchStore.tags && searchStore.tags.length ? 'surprise-me-top-pad' : ''"
  >
    <div class="surprise-me">
      <div class="surprise-me-results">
        <!-- selected video -->
        <div v-if="video" class="surprise-me-main">
          <video-card :video="video" />
        </div>
        <div v-else class="surprise-me-main">
          <skeleton-card />
        </div>
      </div>

      <!-- buttons for shorter, similar, or longer -->
      <div class="surprise-me-submit">
        <div class="filter-option">
          <icon-button
            :classes="`-ico-txt -no-border ${isSelected('shorter') ? '-main' : '-link'}`"
            icon="remove_circle"
            label="Shorter"
            :reduce="!isSelected('shorter')"
            @click-action="changeButtonSelected('shorter')"
          />
        </div>
        <div class="filter-option">
          <icon-button
            :classes="`-ico-txt -no-border ${isSelected('same') ? '-main' : '-link'}`"
            icon="pause_circle"
            :rotate-icon="90"
            label="Similar"
            :reduce="!isSelected('same')"
            @click-action="changeButtonSelected('same')"
          />
        </div>
        <div class="filter-option">
          <icon-button
            :classes="`-ico-txt -no-border ${isSelected('longer') ? '-main' : '-link'}`"
            icon="add_circle"
            label="Longer"
            :reduce="!isSelected('longer')"
            @click-action="changeButtonSelected('longer')"
          />
        </div>
      </div>
      <div class="">
        <p><a :href="videoSearchUrl">New Search Based on this Video</a></p>
      </div>
    </div>

    <div class="surprise-me-close">
      <button class="btn -remove" aria-label="Collapse Surprise Me" @click.prevent="handleClose">
        <span class="iconfont-menu-close"></span>
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { useSearchStore } from '@/stores/search'
import _debounce from 'lodash/debounce'
import VideoCard from './video-card.vue'
import IconButton from '@/components/buttons/icon-button.vue'
import { trackEvent } from '@/utils/analytics'
import type { SimilarVideoType } from '@/types/model-types'
import type { DropdownFilter, DropdownFilterItem } from '@/types/search-types'
import SkeletonCard from '@/components/loaders/skeleton-card.vue'

/**
 * Custom expander that shows a video card with three CTAs to trigger a search
 * for: similar, shorter, longer videos
 *
 * Each button is treated like a drop down option and stored in drop down filters.
 *
 * A tag will be added when a button is used.
 *
 * Emits the following events
 * close-action
 * filter-update
 *
 * Tests
 * The tag is correctly created when button clicked
 * The url is correctly built for them
 * On load from url args, correctly built to pinia and to ui
 * Clearing specific tag clears pinia and UI
 * Clear all from tags clears pinia and UI
 * Clear all from mobile filters clears pinia and UI
 */

/** PROPS **/
const props = defineProps<{
  video: SimilarVideoType
  dropdownFilter: DropdownFilter
}>()

/** STATE **/
const searchStore = useSearchStore()

/** EMITS **/
const emit = defineEmits(['close-action', 'filter-update'])

const videoSearchUrl = computed(() => {
  let searchUrl = '/videos?'

  // do not run computed if video is not set yet to avoid null pointer errors
  if (!props.video) {
    return searchUrl
  }

  const params = []

  // difficulty
  params.push(`difficulty[]=${props.video.difficulty}`)

  // multiple body focus, reformat to url param format
  props.video.bodyfocus_ids.forEach((focusId: number) => {
    params.push(`focus[]=${focusId}`)
  })

  // multiple body focus, reformat to url param format
  props.video.trainingtype_ids.forEach((typeId: number) => {
    params.push(`trainingtype[]=${typeId}`)
  })

  // includes workout + warm up / cool down, only one will be selected
  if (props.video.is_workout_with_both) {
    params.push(`includes=is_workout_with_both`)
  } else if (props.video.is_workout_with_warm_up) {
    params.push(`includes=is_workout_with_warm_up`)
  } else if (props.video.is_workout_with_cool_down) {
    params.push(`includes=is_workout_with_cool_down`)
  } else if (props.video.only_workout) {
    params.push(`includes=only_workout`)
  } else if (props.video.only_warm_up) {
    params.push(`includes=only_warm_up`)
  } else if (props.video.only_cool_down) {
    params.push(`includes=only_cool_down`)
  }
  // end includes

  // add minutes based on search
  // this logic matches what is in VideoApiController::getSimilarVideos
  let minMinutes = props.video.minutes
  let maxMinutes = props.video.minutes

  if (props.dropdownFilter.value === 'shorter') {
    const minDiff = 10
    minMinutes = props.video.minutes - minDiff <= 0 ? 0 : props.video.minutes - minDiff
    maxMinutes = props.video.minutes
  } else if (props.dropdownFilter.value === 'longer') {
    const minDiff = 10
    minMinutes = props.video.minutes
    maxMinutes = props.video.minutes + minDiff
  } else {
    // default same
    const minDiff = 4
    minMinutes = props.video.minutes - minDiff <= 0 ? 0 : props.video.minutes - minDiff
    maxMinutes = props.video.minutes + minDiff
  }

  params.push(`minlength=${minMinutes}`)
  params.push(`maxlength=${maxMinutes}`)
  // end minutes

  return searchUrl + params.join('&')
})

// ... rest of the computed properties and methods ...

/** METHODS **/

// helper do not duplicate logic
function isSelected(time: string) {
  return props.dropdownFilter.value === time
}

function handleClose() {
  emit('close-action')
}

// debounced in case user is rage clicking buttons
const updateSimilarTag = _debounce(() => {
  const selectedOption: DropdownFilterItem | undefined = props.dropdownFilter.items!.find(
    (item) => item.value === props.dropdownFilter.value,
  )

  const tag = searchStore.dropdownTagItem({
    id: props.dropdownFilter.name,
    filterName: props.dropdownFilter.name,
    tagLabel: selectedOption! && selectedOption.label,
    value: selectedOption! && selectedOption.value,
  })

  searchStore.handleTagUpdate(tag)
  emit('filter-update')
}, 500)

function changeButtonSelected(time: string) {
  // only update if changed
  if (props.dropdownFilter.value !== time) {
    // not using v-model as not a real drop down
    // need to update pinia value for filter
    searchStore.setDropdownValue({
      filterName: 'similarTime',
      value: time,
    })

    // debounced before tag updates (which causes search API to update)
    updateSimilarTag()

    // analytics event
    trackEvent({
      category: 'Find Similar',
      action: String(props.video.id),
      label: time,
    })
  }
}
</script>
